import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compact } from 'lodash-es';
import { getPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { getCoreUrl } from 'site-modules/shared/utils/core-link-constructor';
import { ConsumerRating } from 'site-modules/lease-deals/components/lease-deals-details-modal/about-vehicle/consumer-rating';

import './about-vehicle.scss';

export function AboutVehicle({
  msrp,
  engineType = undefined,
  driveType = undefined,
  electricityRange = undefined,
  combinedMpg = undefined,
  seatingCapacity = undefined,
  edmundsRating = undefined,
  makeNiceName,
  modelNiceName,
  year,
}) {
  const formattedMsrp = getPriceStringV2(msrp);

  const isElectric = engineType?.toLowerCase() === 'electric';
  const isPlugInHybrid = engineType?.toLowerCase() === 'plug-in hybrid';
  const isEvOrHybrid = isElectric || isPlugInHybrid;

  const vehicleInfo = compact([
    { label: 'Base MSRP', value: formattedMsrp },
    engineType && driveType
      ? { label: 'Engine', value: `${engineType}, ${driveType}`, className: 'text-capitalize' }
      : undefined,
    isEvOrHybrid
      ? { label: 'EV Range', value: electricityRange ? `${electricityRange} mi` : 'Unavailable' }
      : undefined,
    !isElectric && combinedMpg ? { label: 'MPG', value: `${combinedMpg} MPG Combined` } : undefined,
    seatingCapacity ? { label: 'Total seating', value: `${seatingCapacity} seats` } : undefined,
    edmundsRating
      ? {
          label: 'Edmunds Rating',
          renderValue: (
            <span className="d-flex align-items-center gap-0_5">
              <span className="icon-edmunds-car text-blue-50 size-24" aria-hidden />
              <strong className="text-cool-gray-30">{edmundsRating}/10</strong>
            </span>
          ),
        }
      : undefined,
  ]);

  return (
    <div className="bg-cool-gray-100 rounded-24 p-1_5 about-vehicle">
      <h3 className="size-20 fw-medium mb-1 text-cool-gray-10">About the vehicle</h3>
      <ul className="list-unstyled mb-0">
        {vehicleInfo.map(({ label, value, className, renderValue }, index) => (
          <li
            key={label}
            className={classNames('d-flex justify-content-between py-1', {
              'border-top': index !== 0,
            })}
          >
            <span className="text-cool-gray-40">{label}</span>
            {renderValue ?? <strong className={classNames('text-cool-gray-30', className)}>{value}</strong>}
          </li>
        ))}
        <ConsumerRating make={makeNiceName} model={modelNiceName} year={year} />
      </ul>
      <a
        className="d-flex align-items-center justify-content-end gap-0_25 mt-1 text-cool-gray-30 text-decoration-underline"
        href={getCoreUrl({ makeSlug: makeNiceName, modelSlug: modelNiceName, year })}
        data-tracking-id="view_review"
      >
        <span className="read-review">Read full review</span>
        <i className="icon-arrow-right3 size-12" aria-hidden />
      </a>
    </div>
  );
}

AboutVehicle.propTypes = {
  msrp: PropTypes.number.isRequired,
  makeNiceName: PropTypes.string.isRequired,
  modelNiceName: PropTypes.string.isRequired,
  year: PropTypes.number.isRequired,
  engineType: PropTypes.string,
  driveType: PropTypes.string,
  electricityRange: PropTypes.number,
  combinedMpg: PropTypes.number,
  seatingCapacity: PropTypes.number,
  edmundsRating: PropTypes.number,
};
